import { useCallback, useEffect, useRef, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { Divider } from 'react-native-elements';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { deleteTemplateById, fetchTemplates } from '@/api/services/templates';
import { BodyWrapper } from '@/components/ui/therapist/BodyWrapper';
import { Button } from '@/components/ui/therapist/Button';
import Card from '@/components/ui/therapist/Card';
import { EmptyTemplate } from '@/components/Template/EmptyTemplate';
import { Modal } from '@/components/ui/therapist/Modal';
import { Pagination } from '@/components/ui/therapist/Pagination';
import { Skeleton } from '@/components/Skeleton';
import { CardTemplate } from '@/components/CardTemplate';
import { Text } from '@/components/ui/therapist/Text';
import { logError } from '@/utils/logHandling';
import { TemplateDetailsModal } from '@/components/Template/TemplateDetailsModal';
import { TemplateFilter } from '@/screens/TemplateList/TemplateFilter';
import { TemplatePillSection } from '@/components/Template/TemplatePillSection';
import {
  Template as TemplateType,
  DifficultyQuery,
  TemplateQuery,
} from '@/components/Template/types';
import { TemplateMenu } from './TemplateMenu';
import { useSetEditor } from '@/contexts/SetEditorContext/useSetEditor';

import { styles } from './styles';
import colors from '@/config/colors';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { IS_ANDROID } from '@/utils/platform';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export const TemplateList: React.FC = () => {
  const { showNotification } = useNotification();
  const { navigate } =
    useNavigation<StackNavigationProp<TherapistStackParamList>>();
  const { resetFormData } = useSetEditor();
  const scrollRef = useRef<ScrollView>(null);

  const startNewTemplate = useCallback(() => {
    resetFormData();
    navigate('SetCreation', { flow: 'createTemplate' });
  }, [navigate, resetFormData]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const [difficultyFilter, setDifficultyFilter] = useState<
    DifficultyQuery | undefined
  >();
  const [typeFilter, setTypeFilter] = useState<TemplateQuery | undefined>();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [appliedFilters, setAppliedFilters] = useState<{
    difficulty?: DifficultyQuery | undefined;
    type?: TemplateQuery | undefined;
    name?: string;
    searchTerm?: string;
  }>({});
  const [templates, setTemplates] = useState<TemplateType[]>([]);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [countAppliedFilters, setCountAppliedFilters] = useState<number>(0);
  const [templateToDelete, setTemplateToDelete] = useState<TemplateType | null>(
    null,
  );

  const insets = useSafeAreaInsets();

  const getTemplates = async () => {
    setIsLoading(true);
    const { pageSize, templates, total } = await fetchTemplates({
      page,
      limit: 10,
      difficulty: appliedFilters.difficulty,
      setType: appliedFilters.type,
      name: appliedFilters.name,
      searchTerm: appliedFilters.searchTerm,
    });

    setTemplates(templates);
    setTotalPages(Math.ceil(total / pageSize));
    setIsLoading(false);
  };

  useEffect(() => {
    scrollRef.current?.scrollTo({ y: 0, animated: true });
  }, [page]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ y: 0, animated: true });
  }, [page]);

  useFocusEffect(
    useCallback(() => {
      getTemplates();
    }, [appliedFilters, page]),
  );

  useEffect(() => {
    const concatFilters = (
      (appliedFilters.difficulty || '') + (appliedFilters.type || '')
    )
      .split(',')
      .filter(Boolean).length;

    const titleFilter = appliedFilters.searchTerm ? 1 : 0;
    setCountAppliedFilters(concatFilters + titleFilter);
  }, [appliedFilters]);

  const applyFilters = () => {
    setAppliedFilters({
      difficulty: difficultyFilter,
      type: typeFilter,
      searchTerm,
    });
    setPage(1);
    setShowDrawer(false);
  };

  const resetFilter = () => {
    setDifficultyFilter(undefined);
    setTypeFilter(undefined);
    setSearchTerm('');
    setAppliedFilters({});
    setPage(1);
    setShowDrawer(false);
    setIsLoading(false);
  };

  const handleDeleteTemplate = async () => {
    if (!templateToDelete) {
      return;
    }
    try {
      await deleteTemplateById(templateToDelete.id);
      showNotification({
        type: 'success',
        title: `Vorlage ${templateToDelete.name} gelöscht`,
      });
      setTemplateToDelete(null);
      getTemplates();
    } catch (err) {
      logError('could not delete template:', err);
      setIsLoading(false);
    }
  };

  return (
    <BodyWrapper bodyStyle={{ width: '90%' }} ref={scrollRef}>
      <View style={styles.buttonWrapper}>
        <View style={styles.rightSideButtonWrapper}>
          <Button
            label={
              countAppliedFilters ? `Filter (${countAppliedFilters})` : 'Filter'
            }
            look="secondary"
            onPress={() => setShowDrawer(true)}
            icon="filter"
          />
          <Button
            label="Neue Vorlage erstellen"
            look="primary"
            onPress={startNewTemplate}
            icon="plus"
          />
        </View>
      </View>
      <KeyboardAwareScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ flexGrow: 1 }}
        enableOnAndroid={true}
        extraScrollHeight={IS_ANDROID ? insets.bottom + 150 : 0}
      >
        <Card styleBody={{ padding: 0 }}>
          <View style={{ padding: 20 }}>
            <Text style={styles.header}>Gespeicherte Vorlagen</Text>
            <Text>
              Alle Vorlagen auf einen Blick: Sowohl die von uns hinzugefügten
              als auch Ihre eigenen.
            </Text>
          </View>
          <Divider color={colors.lightGrey} width={2} />
          <View style={styles.grid}>
            {isLoading ? (
              <Skeleton count={10} height={210} />
            ) : templates.length === 0 ? (
              <EmptyTemplate setShowSetTypeModal={startNewTemplate} />
            ) : (
              templates.map((t) => (
                <View style={{ padding: 12, width: '50%' }} key={t.id}>
                  <CardTemplate
                    description={t.description}
                    descriptionBodySection={() => (
                      <TemplateDetailsModal id={t.id} name={t.name} />
                    )}
                    footerSection={() => (
                      <TemplateMenu
                        key={t.id + '-menu'}
                        template={t}
                        handleTemplateDelete={() => setTemplateToDelete(t)}
                      />
                    )}
                    name={t.name}
                    pillsSection={() => <TemplatePillSection template={t} />}
                  />
                </View>
              ))
            )}
          </View>
          <Divider color={colors.grey} width={2} />
          <Pagination
            onPageChange={setPage}
            currentPage={page}
            totalPages={totalPages}
          />
        </Card>
      </KeyboardAwareScrollView>
      <Modal
        isVisible={!!templateToDelete}
        onClose={() => setTemplateToDelete(null)}
        title="Vorlage löschen"
        cancelActionButton={
          <Button
            label="Abbrechen"
            look="tertiary"
            onPress={() => setTemplateToDelete(null)}
          />
        }
        confirmActionButton={
          <Button
            label="Vorlage aus Bibliothek löschen"
            look="danger"
            icon="trash"
            onPress={handleDeleteTemplate}
          />
        }
      >
        <Text style={{ fontSize: 16 }}>
          Das Löschen der Vorlage aus der Bibliothek ist unwiderruflich!
        </Text>
      </Modal>
      <TemplateFilter
        isOpen={showDrawer}
        onClose={applyFilters}
        difficultyFilter={difficultyFilter}
        setDifficultyFilter={setDifficultyFilter}
        typeFilter={typeFilter}
        setTypeFilter={setTypeFilter}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        resetFilter={resetFilter}
      />
    </BodyWrapper>
  );
};
